import { Link } from 'react-router-dom';
import { CopyButton } from '../components/CopyButton';

const MCP_URL = 'https://astdz-mcp.astdz.workers.dev';
const PUBLIC_COMMAND = `claude mcp add --transport http astdz ${MCP_URL}`;
const ENCODE_COMMAND = `claude mcp add --transport http \\\n  --header 'Authorization: Bearer <YOUR_API_KEY>' \\\n  astdz ${MCP_URL}`;

const TOOLS: Array<{ name: string; description: string; access: string }> = [
  { name: 'family_encode', description: 'encode a non-EMRG family payload with supplied context when required', access: 'session or API key' },
  { name: 'family_decode', description: 'decode a non-EMRG family payload within certified schema and tier access', access: 'session or API key' },
  { name: 'family_validate', description: 'return pass/fail and errors for a non-EMRG family payload', access: 'public' },
  { name: 'family_get_definitions', description: 'retrieve complete field definitions and examples', access: 'session only' },
  { name: 'emrg_encode', description: 'encode structured fields with complete text and tier validation', access: 'API key' },
  { name: 'emrg_decode', description: 'decode a complete hex payload and report integrity errors', access: 'public' },
  { name: 'emrg_validate', description: 'check exact length, schema, hex, and CRC16 without an API key', access: 'public' },
  { name: 'emrg_simulate', description: 'build a sample using keyword matching; review the inferred fields', access: 'public' },
  { name: 'search_messages', description: 'search 180 EMRG messages by keyword, category, or exact severity', access: 'public' },
  { name: 'get_event_categories', description: 'list the 15 EMRG event categories', access: 'public' },
  { name: 'get_message', description: 'retrieve a message by decimal or hexadecimal reference', access: 'public' },
];

const EXAMPLE_PROMPTS = [
  'Find flood messages with severity 4.',
  'Simulate a building fire, then validate and decode the complete resulting payload.',
  'What is message reference 0x0001?',
  'List all EMRG event categories.',
];

/** Route: /dashboard/mcp. Remote connector setup and authenticated encoding. */
export function McpPage() {
  return (
    <div>
      <h1 style={{ marginBottom: 12 }}>MCP integration</h1>
      <p style={{ marginBottom: 16 }}>
        Connect an MCP client to 11 tools backed by the all-eleven JavaScript reference codecs and message libraries. The seven legacy EMRG tools are unchanged. Seven tools are public, including family validation.
        Family encode and decode accept a session or API key and enforce matching certification, schema, and tier. Family definitions require a session.
      </p>

      <div className="box" style={{ marginBottom: 16 }}>
        <h2>Claude Desktop or Claude on the web</h2>
        <p>Open Customize → Connectors, add a custom connector, and enter this remote server URL:</p>
        <pre className="hex-display" style={{ overflowX: 'auto' }}>{MCP_URL}</pre>
        <CopyButton text={MCP_URL} label="copy server URL" />
        <p style={{ marginTop: 8 }}>
          Enable the connector in your conversation to use the seven public tools, including EMRG and family validation.
          Team and Enterprise accounts may require an organization owner to add it first.
        </p>
        <p className="text-muted">
          The connector has no OAuth sign-in flow. Use the Claude Code setup below to pass an API key for authenticated operations.
          {' '}<a href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noreferrer">Claude remote connector guide</a>
        </p>
      </div>

      <div className="box" style={{ marginBottom: 16 }}>
        <h2>Claude Code</h2>
        <p>Choose one setup. For the public tools:</p>
        <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto' }}>{PUBLIC_COMMAND}</pre>
        <CopyButton text={PUBLIC_COMMAND} label="copy public setup" />
        <p style={{ marginTop: 12 }}>For the ten tools available with an API key, replace the placeholder with a key from the <Link to="/dashboard/keys">keys page</Link>. The session-only family_get_definitions tool requires a dashboard session token:</p>
        <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto' }}>{ENCODE_COMMAND}</pre>
        <CopyButton text={ENCODE_COMMAND} label="copy encoding setup" />
        <p className="text-muted" style={{ marginTop: 8 }}>
          If astdz is already configured, remove it with <code>claude mcp remove astdz</code> before adding it again.
          Check the connection with <code>claude mcp list</code> or <code>/mcp</code> inside Claude Code.
          {' '}<a href="https://code.claude.com/docs/en/mcp" target="_blank" rel="noreferrer">Claude Code MCP guide</a>
        </p>
      </div>

      <h2>Tools and access</h2>
      <div className="box" style={{ marginBottom: 16 }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {TOOLS.map((tool) => (
            <li key={tool.name} style={{ padding: '6px 0' }}>
              <strong>{tool.name}</strong> <span className="text-muted">({tool.access}) · {tool.description}</span>
            </li>
          ))}
        </ul>
      </div>
      <p style={{ marginBottom: 16 }}>
        EMRG has 51, 115, and 222-byte tiers with 25, 67, and 164 ASCII characters.
        Automatic tier selection preserves the text; a forced tier that cannot fit it returns an error.
        Payload size does not guarantee radio range.
        Family bridge ingest records all supported schemas, but only EMRG traffic can be forwarded to Fastalert. Context supplied for assessment-bearing standards is validated as input; it does not establish the claimant's identity or authority.
      </p>

      <h2>Try these prompts</h2>
      <ul>{EXAMPLE_PROMPTS.map((prompt) => <li key={prompt}>{prompt}</li>)}</ul>
      <p>
        <Link to="/docs/mcp">MCP documentation</Link> · <Link to="/docs/standards/emrg">full EMRG specification</Link> · <Link to="/docs/standards">standards catalog</Link>
      </p>
    </div>
  );
}
